import { useState } from "react";
import { useRecordContext, useTranslate } from "ra-core";
import { EditButton } from "@/components/admin/edit-button";
import { DeleteButton } from "@/components/admin";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { ShowButton } from "@/components/admin/show-button";
import { Button } from "@/components/ui/button";
import { Calendar, MessageSquare, Phone } from "lucide-react";

import { AddTask } from "../tasks/AddTask";
import { TasksIterator } from "../tasks/TasksIterator";
import { TagsListEdit } from "./TagsListEdit";
import { ContactStatusSelector } from "./ContactInputs";
import { ContactPersonalInfo } from "./ContactPersonalInfo";
import { ContactRealEstateInfo } from "./ContactRealEstateInfo";
import { ContactBackgroundInfo } from "./ContactBackgroundInfo";
import { AsideSection } from "../misc/AsideSection";
import type { Contact } from "../types";
import { ContactMergeButton } from "./ContactMergeButton";
import { ExportVCardButton } from "./ExportVCardButton";
import { ContactTelephonyDrawer } from "./ContactTelephonyDrawer";
import { ScheduleVisitDrawer } from "../site-visits/ScheduleVisitDrawer";

export const ContactAside = ({ link = "edit" }: { link?: "edit" | "show" }) => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const [telephonyOpen, setTelephonyOpen] = useState(false);
  const [visitDrawerOpen, setVisitDrawerOpen] = useState(false);

  if (!record) return null;

  const contactName =
    `${record.first_name || ""} ${record.last_name || ""}`.trim() || "Lead";
  const contactPhone =
    record.phone_jsonb?.[0]?.number || record.phone_secondary || "";

  return (
    <div className="hidden sm:block w-92 min-w-92 text-sm">
      <div className="mb-4 -ml-1">
        {link === "edit" ? (
          <EditButton label="resources.contacts.action.edit" />
        ) : (
          <ShowButton label="resources.contacts.action.show" />
        )}
      </div>

      <AsideSection title="Quick Actions">
        <div className="flex flex-col gap-2 py-1">
          <Button
            size="sm"
            className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => setTelephonyOpen(true)}
          >
            <MessageSquare className="w-4 h-4" />
            <Phone className="w-4 h-4" />
            Call & WhatsApp
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="w-full gap-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950"
            onClick={() => setVisitDrawerOpen(true)}
          >
            <Calendar className="w-4 h-4" />
            Schedule Site Visit
          </Button>
        </div>
      </AsideSection>

      <ContactTelephonyDrawer
        open={telephonyOpen}
        onOpenChange={setTelephonyOpen}
        contactName={contactName}
        contactPhone={contactPhone}
        contactId={record.id}
        contact={record}
      />

      <ScheduleVisitDrawer
        isOpen={visitDrawerOpen}
        onOpenChange={setVisitDrawerOpen}
        contactId={String(record.id)}
        contactName={contactName}
        contactPhone={contactPhone}
      />

      <AsideSection title={translate("resources.notes.fields.status")}>
        <ContactStatusSelector />
      </AsideSection>

      <AsideSection
        title={translate("resources.contacts.field_categories.personal_info")}
      >
        <ContactPersonalInfo />
      </AsideSection>

      <AsideSection title="Real Estate Requirements">
        <ContactRealEstateInfo />
      </AsideSection>

      <AsideSection
        title={translate("resources.contacts.field_categories.background_info")}
      >
        <ContactBackgroundInfo />
      </AsideSection>

      <AsideSection
        title={translate("resources.tags.name", { smart_count: 2 })}
      >
        <TagsListEdit />
      </AsideSection>

      <AsideSection
        title={translate("resources.tasks.name", { smart_count: 2 })}
      >
        <ReferenceManyField
          target="contact_id"
          reference="tasks"
          sort={{ field: "due_date", order: "ASC" }}
          perPage={1000}
        >
          <TasksIterator />
        </ReferenceManyField>
        <AddTask />
      </AsideSection>

      {link !== "edit" && (
        <>
          <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
            <ExportVCardButton />
            <ContactMergeButton />
          </div>
          <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
            <DeleteButton
              className="h-6 cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
              size="sm"
            />
          </div>
        </>
      )}
    </div>
  );
};
